import _ from "underscore";
import DriveView from "../DriveView";
import { dispatcher, DispatcherEvents } from "../utils/dispatcher";
import { isNoVehicle } from "../utils/device";
import { show, hide } from "../utils/vanilla";
import { PageLevel } from "../store/mobile/types";
import pageHTML from "./menu.tmpl";

// Temporary hack: in Backbone we set the context and trigger the UserSwitch.
// The context has not finished being updated in the current thread, but the render is executed right away with the previous context.
// Adding a delay here allows the current execution of the context set to finish.
// A better way would be to change how we detect the context change. As we are moving to React, this should be done in that new implementation.
const delayRender = (func: () => {}) => () => setTimeout(func, 1);

export default class MenuView extends DriveView<Backbone.Model> {
    protected strings = [
        "HOS",
        "Work Diary"
    ];

    private readonly template: any = null;
    private hasNewMessages = false;

    constructor (options) {
        super(options);
        this.template = _.template(pageHTML);
        this.storeUnsubscribe = this.app.observeStore(state => [
            state.mobile.pageState.activeSection,
            state.mobile.pageState.pageLevel,
            state.mobile.pageState.isLocked,
            state.mobile.newMessage], this.handleStateChange.bind(this));

        this.listenTo(dispatcher, DispatcherEvents.UserUpdate, this.onUserUpdate.bind(this));
        this.listenTo(dispatcher, DispatcherEvents.UserUpdateLanguage, this.onUserUpdate.bind(this));
        this.listenTo(dispatcher, DispatcherEvents.UserUpdateRuleset, this.render.bind(this));
        this.listenTo(dispatcher, DispatcherEvents.VehicleSelected, this.render.bind(this));
        this.listenTo(dispatcher, DispatcherEvents.UserSwitch, delayRender(this.render.bind(this)));
    }

    /* eslint-disable @typescript-eslint/unbound-method */
    events() {
        return {
            "click .footer-link": this.navigateToPage,
            "keydown .footer-link": this.navigateToPage
        } as Backbone.EventsHash;
    }
    /* eslint-enable @typescript-eslint/unbound-method */

    render() {
        // do not load the menu if there are no user logged in
        if (this.app.store.getState().activeUsers.length === 0) {
            return this;
        }

        let isEWD = this.app.store.getState().data.ELDJurisdiction === "EWD";
        let canAccessHos = this.app.securityService.can("useHos") || (this.app.securityService.can("attemptHos") && isNoVehicle());
        let canAccessDvir = this.app.securityService.can("viewDvir") && !isNoVehicle();
        let canAccessMessaging = this.app.securityService.can("useMessaging");
        let canAccessTrips = this.app.securityService.can("showTrips");

        void this.display(this.template({
            isEWD,
            canAccessHos,
            canAccessDvir,
            canAccessMessaging,
            hasUnreadMessages: this.hasNewMessages,
            canAccessTrips,
            hosLabel: this.getString("HOS"),
            workDiaryLabel: this.getString("Work Diary")
        })).then(() => this.handleStateChange());
        return this;
    }

    private onUserUpdate(user: IUser): void {
        let currentUser = this.app.userService.getCurrentUser();
        if (currentUser && user && currentUser.id === user.id) {
            // Only re-render if the user changing languages is the currently active user
            this.render();
        }
    }

    private navigateToPage(e: Event) {
        if (e.type === "keydown" && (<KeyboardEvent> e).code !== "Enter" && (<KeyboardEvent> e).code !== "Space") {
            return;
        }
        let menuElem = <HTMLElement> this.el;
        menuElem.classList.remove("active");

        let target = e && (e.currentTarget as HTMLLIElement);
        let dataLink = target && target.getAttribute("data-id");
        if (dataLink) {
            this.app.router.navigate(dataLink, true);
            const titleText = <HTMLElement> document.getElementById("title-text");
            titleText && titleText.focus();
        }
    }

    private show() {
        show(this.el);
    }

    private hide() {
        hide(this.el);
    }

    private handleStateChange() {
        if (this.app.store.getState().mobile.pageState.isLocked) {
            this.hide();
            return;
        }

        let pageLevel = this.app.store.getState().mobile.pageState.pageLevel || -1;
        if (pageLevel === PageLevel.ONE) {
            this.show();
        } else {
            this.hide();
            return;
        }

        Array.prototype.forEach.call(this.el.querySelectorAll("li"), (li: HTMLLIElement) => {
            li.classList.remove("active");
            li.setAttribute("aria-selected", "false");
        });

        // With module pages like "modules/driverSafety", replace "/" with "-" as / is not valid in an HTML id
        let activeSection = `menu-${ this.app.store.getState().mobile.pageState.activeSection }`.replace("/", "-") || "";
        if (activeSection.toLowerCase().indexOf("addins") > -1) {
            return;
        }

        let menuButton: HTMLLIElement = activeSection && this.el.querySelector(`#${ activeSection }`);
        if (menuButton) {
            menuButton.classList.add("active");
            menuButton.setAttribute("aria-selected", "true");
        }

        let hasNewMessages = this.app.store.getState().mobile.newMessage;
        if (hasNewMessages !== undefined && this.hasNewMessages !== hasNewMessages) {
            this.hasNewMessages = hasNewMessages;
            this.render();
        }
    }
}
